// src/pages/Users.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { apiFetch } from "../api";
import NavBar from "../components/NavBar";
import AddUserModal from "../components/AddUserModal";
import EditUserModal from "../components/EditUserModal";
import AddClientModal from "../components/AddClientModal";
import InviteClientModal from "../components/InviteClientModal";
import ProfileDetailsModal from "../components/ProfileDetailsModal";
import MultiSelectDropdown from "../components/MultiSelectDropdown";
import { pakistanCities } from "../data/pakistanCities";
import { cityAreasMap } from "../data/cityAreas";
import { useLanguage } from "../context/LanguageContext";
import { useLoading } from "../contexts/LoadingContext";
import { formatDate } from "../utils/dateUtils";
import GenderBadge from "../components/GenderBadge";
import { RefreshIcon, UserPlusIcon, ResetIcon, FilterIcon } from "../components/icons";
import { ProfileGridSkeleton } from "../components/Skeleton";

const createInitialFilters = () => ({
  search: "",
  gender: "",
  maritalStatus: "",
  country: "",
  city: "",
  areas: [],
  minAge: "",
  maxAge: "",
  minIncome: "",
  maxIncome: "",
  profession: "",
  professions: [],
  casts: [],
  education: "",
  houseStatus: "",
  status: "active",
  minViews: "",
  maxViews: "",
  minHeightFeet: "",
  minHeightInches: "",
  maxHeightFeet: "",
  maxHeightInches: "",
  // Default = newest first, mirroring the original implicit sort.
  // Switching to "referenceId asc" lets the bureau scan by ID order.
  sortBy: "createdAt",
  sortDir: "desc",
});

const buildQueryString = (params = {}) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    
    if (Array.isArray(value)) {
      // Handle arrays by appending each item separately
      value.forEach(item => {
        if (item !== undefined && item !== null && item !== "") {
          searchParams.append(key, item);
        }
      });
    } else {
      const stringValue = typeof value === "string" ? value.trim() : `${value}`;
      if (stringValue !== "") searchParams.append(key, stringValue);
    }
  });
  return searchParams.toString();
};

export default function Dashboard() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [banner, setBanner] = useState(null);
  const [availableCasts, setAvailableCasts] = useState([]);
  const [availableProfessions, setAvailableProfessions] = useState([]);
  // Pagination state. `page` is 1-indexed; `pagination` mirrors what the
  // server returns (total, pages, limit). Default page size matches the
  // backend default of 25 — change one, change the other.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 25,
    pages: 1,
  });
  const { setLoading: setGlobalLoading, setGlobalLoadingMessage, isLoading } = useLoading();

  // Detect if public user - check token immediately
  const isPublicUser = localStorage.getItem("publicUserToken") !== null;

  const [filters, setFilters] = useState(createInitialFilters);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
    const [isAddClientModalOpen, setIsAddClientModalOpen] = useState(false);
    const [preselectedClientUser, setPreselectedClientUser] = useState(null);
    // After successfully adding a profile as a client, surface the invite
    // modal automatically so the bureau can immediately send login
    // credentials. Stores the newly-created client (withUserAlias shape)
    // or null when no invite is pending.
    const [inviteClient, setInviteClient] = useState(null);
    const [detailsUserId, setDetailsUserId] = useState(null);
  const { t, isRTL } = useLanguage();

  React.useEffect(() => {
    const onDocClick = (e) => {
      // close if click is not within any kebab menu or its popup
      if (!e.target.closest("[data-kebab]")) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  const filtersRef = useRef(filters);
  const debounceRef = useRef(null);

  const fetchAvailableCasts = useCallback(async () => {
    try {
      const isPublic = localStorage.getItem("publicUserToken") !== null;
      const endpoint = isPublic ? "/public/casts" : "/profiles/casts";
      
      const response = await apiFetch(endpoint);
      
      let casts = [];
      if (Array.isArray(response)) {
        casts = response;
      } else if (response && typeof response === 'object' && response.data) {
        casts = response.data;
      }
      
      // If authenticated endpoint fails, try public endpoint as fallback
      if (!isPublic && casts.length === 0) {
        try {
          const publicResponse = await apiFetch("/public/casts");
          if (Array.isArray(publicResponse)) {
            casts = publicResponse;
          }
        } catch (publicErr) {
          console.error("Public endpoint also failed:", publicErr);
        }
      }
      
      setAvailableCasts(casts.sort());
    } catch (err) {
      console.error("Failed to fetch available casts:", err);
      setAvailableCasts([]);
    }
  }, []);

  const fetchAvailableProfessions = useCallback(async () => {
    try {
      const isPublic = localStorage.getItem("publicUserToken") !== null;
      const endpoint = isPublic ? "/public/professions" : "/profiles/professions";
      
      const response = await apiFetch(endpoint);
      
      let professions = [];
      if (Array.isArray(response)) {
        professions = response;
      } else if (response && typeof response === 'object' && response.data) {
        professions = response.data;
      }
      
      // If authenticated endpoint fails, try public endpoint as fallback
      if (!isPublic && professions.length === 0) {
        try {
          const publicResponse = await apiFetch("/public/professions");
          if (Array.isArray(publicResponse)) {
            professions = publicResponse;
          }
        } catch (publicErr) {
          console.error("Public endpoint also failed:", publicErr);
        }
      }
      
      setAvailableProfessions(professions.sort());
    } catch (err) {
      console.error("Failed to fetch available professions:", err);
      setAvailableProfessions([]);
    }
  }, []);

  const fetchUsers = useCallback(
    async (queryParams, pageOverride, limitOverride) => {
      const params = queryParams ?? filtersRef.current;
      const effectivePage = pageOverride ?? page;
      const effectiveLimit = limitOverride ?? pageSize;
      try {
        if (!isLoading) {
          setLoading(true);
        }

        let response;
        const isPublic = localStorage.getItem("publicUserToken") !== null;

        if (isPublic) {
          const qs = buildQueryString({
            ...params,
            status: "active",
            page: effectivePage,
            limit: effectiveLimit,
          });
          const endpoint = qs
            ? `/public/listings?${qs}`
            : "/public/listings?status=active";
          response = await apiFetch(endpoint);
        } else {
          const qs = buildQueryString({
            ...params,
            page: effectivePage,
            limit: effectiveLimit,
          });
          const endpoint = qs ? `/profiles?${qs}` : "/profiles";
          response = await apiFetch(endpoint);
        }

        // Both endpoints now return { data, pagination }. We still tolerate
        // a bare array in case an older deploy is on the other side.
        const data = Array.isArray(response)
          ? response
          : response?.data || [];
        const meta = response?.pagination;

        setUsers(data);
        if (meta) {
          setPagination(meta);
          // If the server says we're past the last page (e.g. someone
          // deleted the last item on page 5), step back to the last real
          // page so the UI doesn't show empty.
          if (meta.page > meta.pages && meta.pages >= 1) {
            setPage(meta.pages);
          }
        } else {
          setPagination({
            total: data.length,
            page: 1,
            limit: data.length,
            pages: 1,
          });
        }
        setError("");
      } catch (err) {
        console.error("Failed to fetch users:", err);
        setError(err.message || "Unable to load users");
      } finally {
        if (!isLoading) {
          setLoading(false);
        }
      }
    },
    [isLoading, page, pageSize]
  );

  useEffect(() => {
    fetchUsers(filtersRef.current);
    fetchAvailableCasts();
    fetchAvailableProfessions();
  }, [fetchUsers, fetchAvailableCasts, fetchAvailableProfessions]);

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const toggleStatus = async (id, newStatus) => {
    console.log("toggleStatus called with:", { id, newStatus });
    try {
      const message = `${newStatus === "active" ? "Activating" : "Deactivating"} profile...`;
      console.log("Setting spinner message:", message);
      setGlobalLoadingMessage(message);
      console.log("Setting spinner to true");
      setGlobalLoading(true);
      
      console.log("Making API call...");
      await apiFetch(`/profiles/${id}`, {
        method: "PUT",
        body: JSON.stringify({ status: newStatus }),
      });
      console.log("API call completed");
      
      setBanner({
        type: "success",
        text: `Profile ${
          newStatus === "active" ? "activated" : "deactivated"
        }.`,
      });
      
      // Add a small delay to ensure spinner is visible
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Refresh list (deactivated will disappear from active list)
      await fetchUsers();
    } catch (err) {
      console.error("Status update failed:", err);
      setBanner({
        type: "error",
        text: err.message || "Failed to update status",
      });
    } finally {
      console.log("Turning off spinner");
      setGlobalLoading(false);
      setGlobalLoadingMessage("");
    }
  };

  // Any filter / search change resets to page 1 so the user doesn't land
  // on a paginated tail that no longer exists in the narrower result set.
  const scheduleFetch = (nextFilters) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      fetchUsers(nextFilters, 1);
    }, 300);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => {
      const next = { ...prev, [name]: value };
      filtersRef.current = next;
      if (["search", "city"].includes(name)) scheduleFetch(next);
      return next;
    });
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    fetchUsers(filtersRef.current, 1);
  };

  const handleResetFilters = () => {
    const reset = createInitialFilters();
    filtersRef.current = reset;
    setFilters(reset);
    setShowAdvancedFilters(false);
    setPage(1);
    fetchUsers(reset, 1);
  };

  const handleAreaChange = (e) => {
    const { value, checked } = e.target;
    setFilters((prev) => {
      const currentAreas = prev.areas || [];
      let newAreas;
      
      if (checked) {
        // Add area if not already present
        newAreas = currentAreas.includes(value) 
          ? currentAreas 
          : [...currentAreas, value];
      } else {
        // Remove area if present
        newAreas = currentAreas.filter(area => area !== value);
      }
    
      return { ...prev, areas: newAreas };
    });
  };

  const handleSelectAllAreas = () => {
    if (!filters.city || !cityAreasMap[filters.city]) return;
    
    const allAreas = cityAreasMap[filters.city];
    setFilters((prev) => ({ ...prev, areas: allAreas }));
  };

  const handleClearAreas = () => {
    setFilters((prev) => ({ ...prev, areas: [] }));
  };

  const handleCastChange = (e) => {
    const { value, checked } = e.target;
    setFilters((prev) => {
      const currentCasts = prev.casts || [];
      let newCasts;
      
      if (checked) {
        // Add cast if not already present
        newCasts = currentCasts.includes(value) 
          ? currentCasts 
          : [...currentCasts, value];
      } else {
        // Remove cast if present
        newCasts = currentCasts.filter(cast => cast !== value);
      }
      
      return { ...prev, casts: newCasts };
    });
  };

  const handleSelectAllCasts = () => {
    setFilters((prev) => ({ ...prev, casts: availableCasts }));
  };

  const handleClearCasts = () => {
    setFilters((prev) => ({ ...prev, casts: [] }));
  };

  const handleProfessionChange = (e) => {
    const { value, checked } = e.target;
    setFilters((prev) => {
      const currentProfessions = prev.professions || [];
      let newProfessions;
      
      if (checked) {
        // Add profession if not already present
        newProfessions = currentProfessions.includes(value) 
          ? currentProfessions 
          : [...currentProfessions, value];
      } else {
        // Remove profession if present
        newProfessions = currentProfessions.filter(profession => profession !== value);
      }
      
      return { ...prev, professions: newProfessions };
    });
  };

  const handleSelectAllProfessions = () => {
    setFilters((prev) => ({ ...prev, professions: availableProfessions }));
  };

  const handleClearProfessions = () => {
    setFilters((prev) => ({ ...prev, professions: [] }));
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    try {
      setGlobalLoadingMessage("Deleting profile...");
      setGlobalLoading(true);
      
      await apiFetch(`/profiles/${id}`, { method: "DELETE" });
      setBanner({
        type: "success",
        text: "User deleted successfully.",
      });
      // Refresh list (deactivated will disappear from active list)
      await fetchUsers();
    } catch (err) {
      console.error("Delete failed:", err);
      setBanner({
        type: "error",
        text: err.message || "Failed to delete user",
      });
    } finally {
      setGlobalLoading(false);
      setGlobalLoadingMessage("");
    }
  };

  const handleWhatsAppShare = (user) => {
    // Create comprehensive profile message with all available details
    const profileMessage = `*${user.fullName}* (${t('id')}: ${user.referenceId})

📞 *${t('contactNumber')}:* ${user.contactNumber}

👤 *${t('personalDetails')}:*
• ${t('age')}: ${user.age ? user.age + ' ' + t('years') : t('notSpecified')}
• ${t('gender')}: ${user.gender === 'male' ? t('male') : user.gender === 'female' ? t('female') : user.gender}
• ${t('height')}: ${user.heightFeet && user.heightInches ? `${user.heightFeet}${t('feet')} ${user.heightInches}${t('inches')}` : t('notSpecified')}
• ${t('maritalStatus')}: ${user.maritalStatus ? t(user.maritalStatus) : t('notSpecified')}
• ${t('dateOfBirth')}: ${user.dob ? formatDate(user.dob) : t('notSpecified')}

🏠 *Location & House:*
• ${t('city')}: ${user.city || t('notSpecified')}
• ${t('country')}: ${user.country || t('notSpecified')}
• ${t('area')}: ${user.area || t('notSpecified')}
• House Location: ${user.houseLocation || t('notSpecified')}
• ${t('houseSize')}: ${user.houseSizeMarla ? `${user.houseSizeMarla} ${t('marla')}` : t('notSpecified')}
• ${t('houseStatus')}: ${user.houseStatus ? t(user.houseStatus) : t('notSpecified')}

💼 *Professional Details:*
• ${t('profession')}: ${user.profession || t('notSpecified')}
• ${t('monthlyIncome')}: ${user.monthlyIncome ? `Rs${user.monthlyIncome.toLocaleString()}` : t('notSpecified')}
• ${t('education')}: ${user.education || t('notSpecified')}
• Institute: ${user.instituteOfQualification || t('notSpecified')}

👨‍👩‍👧‍👦 *${t('familyInformation')}:*
• ${t('caste')}: ${user.caste || t('notSpecified')}
• ${t('father')}: ${user.fatherOccupation || t('notSpecified')}${user.fatherDeceased ? ' (Deceased)' : ''}
• ${t('mother')}: ${user.motherOccupation || t('notSpecified')}${user.motherDeceased ? ' (Deceased)' : ''}
• Brothers: ${user.brothersCount || 0}
• Sisters: ${user.sistersCount || 0}

🔗 *${t('viewProfile')}:* ${window.location.origin}/public-profiles

📋 *${t('sharedVia')}*`;
    
    // Create WhatsApp share URL
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(profileMessage)}`;
    
    // Open WhatsApp in a new tab
    window.open(whatsappUrl, '_blank');
  };

  const handleAddAsClient = (user) => {
    setPreselectedClientUser(user || null);
    setIsAddClientModalOpen(true);
  };

  const handleAddClient = async (clientData) => {
    try {
      setGlobalLoadingMessage("Adding client...");
      setGlobalLoading(true);

      // Create client via API. The backend returns the newly-promoted User
      // in withUserAlias shape ({_id, user, publicUser, ...}) — exactly what
      // InviteClientModal expects, so we hand it straight through.
      const res = await apiFetch("/clients", {
        method: "POST",
        body: JSON.stringify(clientData),
      });

      setIsAddClientModalOpen(false);
      setPreselectedClientUser(null);
      setBanner({
        type: "success",
        text: "Client added successfully. Send login credentials now?",
      });

      // Auto-surface the invite modal so the bureau can send the login
      // credentials in the same flow. They can dismiss to skip.
      if (res?.data) {
        setInviteClient(res.data);
      }
    } catch (err) {
      console.error("Failed to add client:", err);
      setBanner({
        type: "error",
        text: err.message || "Failed to add client",
      });
    } finally {
      setGlobalLoading(false);
      setGlobalLoadingMessage("");
    }
  };

  const handleInviteClient = async (clientId, inviteData) => {
    try {
      setGlobalLoadingMessage("Inviting client...");
      setGlobalLoading(true);
      const data = await apiFetch(`/clients/${clientId}/invite`, {
        method: "POST",
        body: JSON.stringify(inviteData),
      });
      setBanner({
        type: "success",
        text: data?.data?.emailSent
          ? "Invite sent — login credentials emailed to the client."
          : "Client invite created. Email delivery may have failed; share credentials manually if needed.",
      });
      // Refresh the user/client list so the linked publicUser badge appears.
      try {
        await fetchUsers();
      } catch (_) {
        /* non-fatal */
      }
      return data;
    } catch (err) {
      console.error("Failed to invite client:", err);
      throw err;
    } finally {
      setGlobalLoading(false);
      setGlobalLoadingMessage("");
    }
  };

  const onSaved = async (msg) => {
    setBanner({ type: "success", text: msg || "Saved." });
    await fetchUsers();

    // Bureau-only background sync: skip for public users — these endpoints
    // are bureau-protected and would 401 (which would auto-logout the user
    // via the api.js interceptor).
    if (isPublicUser) return;

    // Check if the updated user is linked to any clients and update them
    try {
      // Get all clients to check if any are linked to this user
      const clientsResponse = await apiFetch("/clients");
      const clients = clientsResponse.data || [];

      // Find clients that have this user as their linked user
      const linkedClients = clients.filter(client =>
        client.user && client.user._id === editUser?._id
      );

      // Update each linked client with the latest user information
      for (const client of linkedClients) {
        await apiFetch(`/clients/${client._id}`, {
          method: "PUT",
          body: JSON.stringify({
            user: editUser._id,
            notes: client.notes || `Updated profile: ${editUser.fullName} (ID: ${editUser.referenceId})`,
          }),
        });
      }
    } catch (err) {
      console.error("Failed to update linked clients:", err);
      // Don't show error to user as this is background operation
    }
  };

  const bannerClasses =
    banner?.type === "success"
      ? "border-[var(--color-notice-border)] bg-[var(--color-notice-bg)] text-[var(--color-notice-text)]"
      : "border-[var(--color-danger-border)] bg-[var(--color-danger-bg)] text-[var(--color-danger-text)]";

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <NavBar onEditProfile={onSaved} />

      {/* Sticky page header — stays below the NavBar while scrolling.
          z-20 keeps it above page content (including profile-card kebab
          dropdowns at z-10) and below the mobile-menu overlay so opening
          the hamburger still covers it. */}
      <div className="sticky top-16 z-20 bg-[var(--color-background)] border-b border-[var(--color-border)]">
        <div className={`flex flex-wrap items-center justify-between gap-3 px-6 py-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <div>
            <h2 className="text-base sm:text-lg md:text-xl font-semibold text-[var(--color-text-strong)]">
              {isPublicUser ? t('publicProfiles') : t('profiles')}
            </h2>
            <p className="mt-1 text-sm text-[var(--color-text-muted)]">
              {isPublicUser
                ? t('exploreProfiles')
                : t('cardViewDescription')}
            </p>
          </div>

          <div className={`flex flex-wrap items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <button
              onClick={() => fetchUsers()}
              disabled={loading}
              className="whitespace-nowrap inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] text-[var(--color-text-strong)] hover:bg-[var(--color-surface-hover)] disabled:opacity-60 px-3 py-2 text-sm font-semibold transition-colors"
            >
              <RefreshIcon className="w-4 h-4" />
              {loading ? t('loading') : t('refresh')}
            </button>

            {!isPublicUser && (
              <button
                onClick={() => setIsAddOpen(true)}
                className="whitespace-nowrap inline-flex items-center justify-center gap-2 rounded-lg bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-[var(--color-on-primary)] px-4 py-2 text-sm font-semibold shadow-sm transition-colors"
              >
                <UserPlusIcon className="w-4 h-4" />
                {t('addUser')}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="relative p-6 space-y-6">
        {banner && (
          <div
            className={`flex items-start justify-between gap-4 rounded-xl border px-4 py-3 text-sm shadow-sm ${bannerClasses}`}
          >
            <span>{banner.text}</span>
            <button
              className="text-xs font-semibold uppercase tracking-wide"
              onClick={() => setBanner(null)}
            >
              Dismiss
            </button>
          </div>
        )}

        <div className={`overflow-hidden rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] shadow-lg ${isRTL ? 'rtl' : 'ltr'}`}>
          {/* Filters - Show for both public users and bureau admins */}
          <form
            onSubmit={handleFilterSubmit}
            className={`space-y-4 border-b border-[var(--color-border)] px-6 py-4 ${isRTL ? 'text-right' : ''}`}
          >
            <div className={`grid gap-4 md:grid-cols-3 ${isRTL ? 'text-right' : ''}`}>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                  {t('search')}
                </label>
                <input
                  name="search"
                  value={filters.search}
                  onChange={handleFilterChange}
                  placeholder="Search by referenceId, name or contact..."
                  className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm"
                />
              </div>
              {/* Sort dropdown — single field for the four allowed
                  combinations (ID asc/desc, Date Added asc/desc). One
                  combined select reads cleaner in the filter bar than
                  two separate "field + direction" controls. The value
                  is `${sortBy}:${sortDir}` so a single state change
                  updates both filter fields atomically. */}
              <div>
                <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                  Sort
                </label>
                <select
                  name="sort"
                  value={`${filters.sortBy}:${filters.sortDir}`}
                  onChange={(e) => {
                    const [sortBy, sortDir] = e.target.value.split(":");
                    setFilters((prev) => {
                      const next = { ...prev, sortBy, sortDir };
                      filtersRef.current = next;
                      // Re-fetch immediately on sort change so the user
                      // doesn't have to click Apply for what feels like
                      // an instant UI control.
                      fetchUsers(next);
                      return next;
                    });
                  }}
                  className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm"
                >
                  <option value="createdAt:desc">Date Added — Newest first</option>
                  <option value="createdAt:asc">Date Added — Oldest first</option>
                  <option value="referenceId:asc">ID — Ascending</option>
                  <option value="referenceId:desc">ID — Descending</option>
                </select>
              </div>
            </div>
  {showAdvancedFilters && (
            <>
              <div className="grid gap-4 md:grid-cols-2 mt-4">
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                    Country
                  </label>
                  <input
                    type="text"
                    name="country"
                    value={filters.country}
                    onChange={handleFilterChange}
                    placeholder="e.g. Pakistan"
                    autoCapitalize="off"
                    autoCorrect="off"
                    className="w-full rounded-lg border px-3 py-2 text-sm"
                  />
                </div>
                   <div>
                  <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                    City
                  </label>
                  <select
                    name="city"
                    value={filters.city}
                    onChange={handleFilterChange}
                    className="w-full rounded-lg border px-3 py-2 text-sm"
                  >
                    <option value="">Select City</option>
                    <optgroup label="Popular Cities">
                      {pakistanCities.popular.map((city) => (
                        <option key={city} value={city}>
                          {city}
                        </option>
                      ))}
                    </optgroup>

                  </select>
                </div>

                {filters.city && cityAreasMap[filters.city] && (
                  <div >
                    <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                      Areas (Select Multiple)
                    </label>
                    <div className={`space-y-2 border rounded-lg p-2 ${cityAreasMap[filters.city] && cityAreasMap[filters.city].length > 5 ? 'max-h-[120px] overflow-y-auto' : 'min-h-[60px]'}`}>
                      {cityAreasMap[filters.city].map((area) => (
                        <label key={area} className="flex items-center space-x-2 cursor-pointer hover:bg-[var(--color-surface-hover)] p-1 rounded">
                          <input
                            type="checkbox"
                            name="areas"
                            value={area}
                            checked={filters.areas.includes(area)}
                            onChange={handleAreaChange}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm">{area}</span>
                        </label>
                      ))}
                    </div>
                    <div className="flex justify-between mt-2">
                      <button
                        type="button"
                        onClick={handleSelectAllAreas}
                        className="text-xs text-blue-600 hover:text-blue-800"
                      >
                        Select All
                      </button>
                      <button
                        type="button"
                        onClick={handleClearAreas}
                        className="text-xs text-red-600 hover:text-red-800"
                      >
                        Clear All
                      </button>
                    </div>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                    Gender
                  </label>
                  <select
                    name="gender"
                    value={filters.gender}
                    onChange={handleFilterChange}
                    className="w-full rounded-lg border px-3 py-2 text-sm"
                  >
                    <option value="">All</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                    Marital Status
                  </label>
                  <select
                    name="maritalStatus"
                    value={filters.maritalStatus}
                    onChange={handleFilterChange}
                    className="w-full rounded-lg border px-3 py-2 text-sm"
                  >
                    <option value="">All</option>
                    <option value="single">Single</option>
                    <option value="divorced">Divorced</option>
                    <option value="widowed">Widowed</option>  
                    <option value="seperated">Seperated</option>
                  </select>
                </div>

              <div>
                  <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                    Height Min
                  </label>
                  <div className="flex gap-2">
                    <select
                      name="minHeightFeet"
                      value={filters.minHeightFeet}
                      onChange={handleFilterChange}
                      className="w-1/2 rounded-lg border px-3 py-2 text-sm"
                    >
                      <option value="">Feet</option>
                      {[...Array(8)].map((_, i) => (
                        <option key={i + 3} value={i + 3}>
                          {i + 3}
                        </option>
                      ))}
                    </select>
                    <select
                      name="minHeightInches"
                      value={filters.minHeightInches}
                      onChange={handleFilterChange}
                      className="w-1/2 rounded-lg border px-3 py-2 text-sm"
                    >
                      <option value="">Inches</option>
                      {[...Array(12)].map((_, i) => (
                        <option key={i} value={i}>
                          {i}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                    Height Max
                  </label>
                  <div className="flex gap-2">
                    <select
                      name="maxHeightFeet"
                      value={filters.maxHeightFeet}
                      onChange={handleFilterChange}
                      className="w-1/2 rounded-lg border px-3 py-2 text-sm"
                    >
                      <option value="">Feet</option>
                      {[...Array(8)].map((_, i) => (
                        <option key={i + 3} value={i + 3}>
                          {i + 3}
                        </option>
                      ))}
                    </select>
                    <select
                      name="maxHeightInches"
                      value={filters.maxHeightInches}
                      onChange={handleFilterChange}
                      className="w-1/2 rounded-lg border px-3 py-2 text-sm"
                    >
                      <option value="">Inches</option>
                      {[...Array(12)].map((_, i) => (
                        <option key={i} value={i}>
                          {i}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                      Age Min
                    </label>
                    <input
                      type="number"
                      min="18"
                      name="minAge"
                      value={filters.minAge}
                      onChange={handleFilterChange}
                      className="w-full rounded-lg border px-3 py-2 text-sm"
                    />
                  </div>

                  <div className="flex-1">
                    <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                      Age Max
                    </label>
                    <input
                      type="number"
                      min="18"
                      name="maxAge"
                      value={filters.maxAge}
                      onChange={handleFilterChange}
                      className="w-full rounded-lg border px-3 py-2 text-sm"
                    />
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                      Monthly Income Min (Rs)
                    </label>
                    <input
                      type="number"
                      min="0"
                      name="minIncome"
                      value={filters.minIncome}
                      onChange={handleFilterChange}
                      className="w-full rounded-lg border px-3 py-2 text-sm"
                      placeholder="Min income"
                    />
                  </div>

                  <div className="flex-1">
                    <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                      Monthly Income Max (Rs)
                    </label>
                    <input
                      type="number"
                      min="0"
                      name="maxIncome"
                      value={filters.maxIncome}
                      onChange={handleFilterChange}
                      className="w-full rounded-lg border px-3 py-2 text-sm"
                      placeholder="Max income"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                    Education
                  </label>
                  <input
                    name="education"
                    value={filters.education}
                    onChange={handleFilterChange}
                    className="w-full rounded-lg border px-3 py-2 text-sm"
                    placeholder="e.g. Bachelors"
                  />
                </div>

                {/* Force Profession + Cast/Caste onto a single shared row,
                    regardless of where they fall in the outer 2-col grid
                    flow. Both span the full width of the parent grid and
                    sub-divide into their own 2 columns. */}
                <div className="md:col-span-2 grid gap-4 md:grid-cols-2">
                  <MultiSelectDropdown
                    label="Profession"
                    options={availableProfessions}
                    selected={filters.professions}
                    onToggle={handleProfessionChange}
                    onSelectAll={handleSelectAllProfessions}
                    onClearAll={handleClearProfessions}
                    emptyText="No professions yet. Add profiles with profession info first."
                  />

                  <MultiSelectDropdown
                    label="Cast / Caste"
                    options={availableCasts}
                    selected={filters.casts}
                    onToggle={handleCastChange}
                    onSelectAll={handleSelectAllCasts}
                    onClearAll={handleClearCasts}
                    emptyText="No casts yet. Add profiles with cast info first."
                  />
                </div>

                <div className="mt-6">
                  <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                    House Status
                  </label>
                  <select
                    name="houseStatus"
                    value={filters.houseStatus}
                    onChange={handleFilterChange}
                    className="w-full rounded-lg border px-3 py-2 text-sm"
                  >
                    <option value="">Any</option>
                    <option value="rent">Rent</option>
                    <option value="own">Own</option>
                  </select>
                </div>

                <div className="mt-6">
                  <label className="block text-sm font-medium text-[var(--color-text-muted)]">
                    Status
                  </label>
                  <select
                    name="status"
                    value={filters.status}
                    onChange={handleFilterChange}
                    className="w-full rounded-lg border px-3 py-2 text-sm"
                  >
                    <option value="">Any</option>
                    <option value="active">Active</option>
                    <option value="nonactive">Nonactive</option>
                  </select>
                </div>

              </div>

              {/* Action buttons live inside the advanced filters panel so
                  they're only visible when the panel is open. */}
              <div className="flex justify-end gap-3 mt-4">
                <button
                  type="button"
                  onClick={handleResetFilters}
                  className="whitespace-nowrap inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] text-[var(--color-text)] hover:bg-[var(--color-surface-hover)] px-4 py-2 text-sm font-semibold transition-colors"
                >
                  <ResetIcon className="w-4 h-4" />
                  Reset
                </button>
                <button
                  type="submit"
                  className="whitespace-nowrap inline-flex items-center gap-2 rounded-lg bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-[var(--color-on-primary)] px-4 py-2 text-sm font-semibold transition-colors"
                >
                  <FilterIcon className="w-4 h-4" />
                  Apply Filters
                </button>
              </div>
            </>
            )}
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setShowAdvancedFilters((s) => !s)}
                className="text-sm text-[var(--color-primary)] hover:text-[var(--color-primary-hover)] transition-colors"
              >
                {showAdvancedFilters
                  ? "Hide advanced filters"
                  : "Show advanced filters"}
              </button>
            </div>

          
          </form>

          {/* Cards grid or Table view */}
          <div className="p-6">
            {error && (
              <div className="mb-4 rounded-lg border border-[var(--color-danger-border)] bg-[var(--color-danger-bg)] px-3 py-2 text-sm text-[var(--color-danger-text)]">
                {error}
              </div>
            )}

            {!loading && users.length === 0 && (
              <div className="text-center text-[var(--color-text-muted)] py-10">
                {isPublicUser
                  ? "No saved profiles yet."
                  : "No users found. Add one to get started."}
              </div>
            )}

            {/* Skeleton mounted ONLY when the list is empty (initial
                load). On a refresh we keep the previous results visible
                so the screen doesn't flash — the user can tell from the
                navbar/loading state that a fetch is in flight. */}
            {loading && users.length === 0 && <ProfileGridSkeleton count={6} />}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {users.map((u) => (
                  <div
                    key={u._id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setDetailsUserId(u._id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setDetailsUserId(u._id);
                      }
                    }}
                    className="relative overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-4 hover:shadow-md hover:border-[var(--color-primary)] cursor-pointer transition-all focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                  >
                    {/* Quick action: Add as Client (icon + tooltip) */}
                    {!isPublicUser && !u.isActiveClient && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddAsClient(u);
                        }}
                        title={u.isActiveClient ? "Already a client" : "Add as client"}
                        aria-label="Add as client"
                        className="absolute top-3 right-12 z-10 h-8 w-8 rounded-full bg-[var(--color-surface)] border border-[var(--color-border)] flex items-center justify-center shadow-sm text-[var(--color-primary)] hover:bg-[var(--color-surface-hover)] transition-colors"
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                          <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                          <circle cx="8.5" cy="7" r="4" />
                          <line x1="20" y1="8" x2="20" y2="14" />
                          <line x1="23" y1="11" x2="17" y2="11" />
                        </svg>
                      </button>
                    )}

                    {/* Kebab menu (Edit / Deactivate / Delete / WhatsApp) */}
                    {!isPublicUser && (
                      <div
                        className="absolute top-3 right-3 z-10"
                        data-kebab={u._id}
                      >
                        <div className="relative">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenMenuId(
                                openMenuId === u._id ? null : u._id,
                              );
                            }}
                            aria-haspopup="true"
                            aria-expanded={openMenuId === u._id}
                            className="h-8 w-8 rounded-full bg-[var(--color-surface)] border border-[var(--color-border)] flex items-center justify-center shadow-sm hover:scale-105"
                            title="Options"
                          >
                            ⋮
                          </button>

                          {openMenuId === u._id && (
                            <div
                              role="menu"
                              className="absolute right-0 mt-2 w-44 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] shadow-lg z-10"
                            >
                              <button
                                role="menuitem"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditUser(u);
                                  setOpenMenuId(null);
                                }}
                                className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-[var(--color-surface-hover)] ${isRTL ? 'flex-row-reverse text-right' : 'text-left'}`}
                              >
                                {/* Pencil icon */}
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 shrink-0">
                                  <path d="M12 20h9" />
                                  <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                                </svg>
                                <span>{t('edit')}</span>
                              </button>
                              {u.status === "active" ? (
                                <button
                                  role="menuitem"
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    if (
                                      !window.confirm(isRTL ? "اس پروفائل کو غیر فعال کریں؟" : "Deactivate this profile?")
                                    )
                                      return;
                                    await toggleStatus(u._id, "nonactive");
                                    setOpenMenuId(null);
                                  }}
                                  className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-[var(--color-surface-hover)] ${isRTL ? 'flex-row-reverse text-right' : 'text-left'}`}
                                >
                                  {/* Eye-off icon */}
                                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 shrink-0">
                                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                                    <line x1="1" y1="1" x2="23" y2="23" />
                                  </svg>
                                  <span>{t('deactivate')}</span>
                                </button>
                              ) : (
                                <button
                                  role="menuitem"
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    if (
                                      !window.confirm(isRTL ? "اس پروفائل کو فعال کریں؟" : "Activate this profile?")
                                    )
                                      return;
                                    await toggleStatus(u._id, "active");
                                    setOpenMenuId(null);
                                  }}
                                  className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-[var(--color-surface-hover)] ${isRTL ? 'flex-row-reverse text-right' : 'text-left'}`}
                                >
                                  {/* Eye icon */}
                                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 shrink-0">
                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                    <circle cx="12" cy="12" r="3" />
                                  </svg>
                                  <span>{t('activate')}</span>
                                </button>
                              )}
                              <button
                                role="menuitem"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(u._id);
                                  setOpenMenuId(null);
                                }}
                                className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-[var(--color-danger-text)] hover:bg-[var(--color-surface-hover)] ${isRTL ? 'flex-row-reverse text-right' : 'text-left'}`}
                              >
                                {/* Trash icon */}
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 shrink-0">
                                  <polyline points="3 6 5 6 21 6" />
                                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                                  <path d="M10 11v6" />
                                  <path d="M14 11v6" />
                                  <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
                                </svg>
                                <span>{t('delete')}</span>
                              </button>
                              <button
                                role="menuitem"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleWhatsAppShare(u);
                                  setOpenMenuId(null);
                                }}
                                className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-[var(--color-surface-hover)] text-green-600 ${isRTL ? 'flex-row-reverse text-right' : 'text-left'}`}
                              >
                                {/* Share icon */}
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 shrink-0">
                                  <circle cx="18" cy="5" r="3" />
                                  <circle cx="6" cy="12" r="3" />
                                  <circle cx="18" cy="19" r="3" />
                                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                                  <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                                </svg>
                                <span>{t('shareOnWhatsApp')}</span>
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Compact card body — matches the seeker-side FindMatches
                        layout. Click the card to open ProfileDetailsModal with
                        the full record. Corner buttons (kebab, add-as-client)
                        still stopPropagation so they don't trigger the modal. */}
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)] mb-0.5">
                      {t('id')} {u.referenceId || "—"}
                    </div>
                    <div className="flex items-center gap-2 mb-3 pr-16">
                      {u.fullName && (
                        <h3 className="text-base font-semibold text-[var(--color-text-strong)] truncate">
                          {u.fullName}
                        </h3>
                      )}
                      <GenderBadge gender={u.gender} />
                    </div>

                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between gap-2">
                        <span className="text-[var(--color-text-muted)]">{t('age') || 'Age'}</span>
                        <span className="text-[var(--color-text)] font-medium">
                          {u.age ?? "—"}
                        </span>
                      </div>
                      {(u.heightFeet || u.heightInches) && (
                        <div className="flex justify-between gap-2">
                          <span className="text-[var(--color-text-muted)]">Height</span>
                          <span className="text-[var(--color-text)] font-medium">
                            {u.heightFeet ?? "—"}ft {u.heightInches ?? 0}in
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between gap-2">
                        <span className="text-[var(--color-text-muted)]">City</span>
                        <span className="text-[var(--color-text)] font-medium truncate">
                          {u.city || "—"}
                        </span>
                      </div>
                      {u.country && (
                        <div className="flex justify-between gap-2">
                          <span className="text-[var(--color-text-muted)]">Country</span>
                          <span className="text-[var(--color-text)] font-medium truncate">
                            {u.country}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between gap-2">
                        <span className="text-[var(--color-text-muted)]">{t('profession') || 'Profession'}</span>
                        <span className="text-[var(--color-text)] font-medium truncate">
                          {u.profession || "—"}
                        </span>
                      </div>
                      {(u.cast || u.caste) && (
                        <div className="flex justify-between gap-2">
                          <span className="text-[var(--color-text-muted)]">{t('caste') || 'Caste'}</span>
                          <span className="text-[var(--color-text)] font-medium truncate">
                            {u.cast || u.caste}
                          </span>
                        </div>
                      )}
                      {u.maritalStatus && (
                        <div className="flex justify-between gap-2">
                          <span className="text-[var(--color-text-muted)]">Marital</span>
                          <span className="text-[var(--color-text)] font-medium capitalize">
                            {t(u.maritalStatus) || u.maritalStatus}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="mt-3 pt-3 border-t border-[var(--color-border)] flex items-center justify-between gap-2 text-[10px] text-[var(--color-text-muted)]">
                      <span className="truncate">
                        Added {formatDate(u.createdAt)} · {u.status || "active"}
                      </span>
                      <span className="inline-flex items-center gap-1 shrink-0">
                        <svg
                          viewBox="0 0 24 24"
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                        {u.views ?? 0}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination — server-side. Renders only when there's more
                  than one page to walk. Shows "Showing X–Y of Z" + Prev /
                  page indicator / Next + a page-size picker. */}
              {pagination.total > 0 && pagination.pages > 1 && (
                <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-1">
                  <div className="text-xs text-[var(--color-text-muted)]">
                    Showing{" "}
                    <span className="font-semibold text-[var(--color-text)]">
                      {(pagination.page - 1) * pagination.limit + 1}
                    </span>
                    {"–"}
                    <span className="font-semibold text-[var(--color-text)]">
                      {Math.min(
                        pagination.page * pagination.limit,
                        pagination.total
                      )}
                    </span>{" "}
                    of{" "}
                    <span className="font-semibold text-[var(--color-text)]">
                      {pagination.total}
                    </span>{" "}
                    profiles
                  </div>

                  <div className="flex items-center gap-2">
                    <label className="text-xs text-[var(--color-text-muted)] flex items-center gap-1">
                      Per page
                      <select
                        value={pageSize}
                        onChange={(e) => {
                          const next = Number(e.target.value) || 25;
                          setPageSize(next);
                          setPage(1);
                          fetchUsers(filtersRef.current, 1, next);
                        }}
                        className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-1 text-xs"
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                    </label>

                    <button
                      type="button"
                      onClick={() => {
                        const prev = Math.max(1, page - 1);
                        setPage(prev);
                        fetchUsers(filtersRef.current, prev);
                      }}
                      disabled={page <= 1 || loading}
                      className="rounded-lg border border-[var(--color-border)] px-3 py-1.5 text-xs font-medium text-[var(--color-text)] hover:bg-[var(--color-surface-hover)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      ← Prev
                    </button>

                    <span className="text-xs text-[var(--color-text-muted)]">
                      Page{" "}
                      <span className="font-semibold text-[var(--color-text)]">
                        {pagination.page}
                      </span>{" "}
                      of{" "}
                      <span className="font-semibold text-[var(--color-text)]">
                        {pagination.pages}
                      </span>
                    </span>

                    <button
                      type="button"
                      onClick={() => {
                        const nxt = Math.min(pagination.pages, page + 1);
                        setPage(nxt);
                        fetchUsers(filtersRef.current, nxt);
                      }}
                      disabled={page >= pagination.pages || loading}
                      className="rounded-lg border border-[var(--color-border)] px-3 py-1.5 text-xs font-medium text-[var(--color-text)] hover:bg-[var(--color-surface-hover)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      Next →
                    </button>
                  </div>
                </div>
              )}
        </div>
        </div>
      </div>

      {!isPublicUser && (
        <>
          <AddUserModal
            isOpen={isAddOpen}
            onClose={() => setIsAddOpen(false)}
            onSaved={() => {
              setIsAddOpen(false);
              onSaved("User created successfully.");
            }}
          />

          {editUser && (
            <EditUserModal
              isOpen={!!editUser}
              initialData={editUser}
              onClose={() => setEditUser(null)}
              onSaved={() => {
                setEditUser(null);
                onSaved("User updated successfully.");
              }}
            />
          )}
          {isAddClientModalOpen && (
            <AddClientModal
              isOpen={isAddClientModalOpen}
              preselectedUser={preselectedClientUser}
              onClose={() => {
                setIsAddClientModalOpen(false);
                setPreselectedClientUser(null);
              }}
              onAddClient={handleAddClient}
            />
          )}
          {/* Invite modal — auto-opens right after a client is added so the
              bureau can send login credentials in one flow. Closing it just
              skips the invite; the client itself was already created. */}
          <InviteClientModal
            isOpen={Boolean(inviteClient)}
            client={inviteClient}
            onClose={() => setInviteClient(null)}
            onInvite={handleInviteClient}
          />
        </>
      )}

      {/* Card-click → full profile details. Shared with FindMatches. */}
      <ProfileDetailsModal
        isOpen={!!detailsUserId}
        userId={detailsUserId}
        onClose={() => setDetailsUserId(null)}
      />
    </div>
  );
};
